import { useEffect, useContext, useState } from 'react';
import { Flexbox, Button } from '@sede-enterprise/shell-ds-react-framework';
import { filterData as fetchFilterData } from '../../../../../api/services';
import { AppContext } from '../../../../../Context/AppContext';
import { SelectFormField } from '../../../../ReUsableComponents/SelectFormField/SelectFormField';
import {
  Commodity,
  Country,
  IAnalyticsSnapshotReportFilteredValues,
} from '../../../../../Types/IAnalyticsSnapshotReportType';
import { getCombinedMonths } from '../../../../Utils/utils';
import { getUpdatedCountrySelection } from '../../NcfReportingUtils/NcfReportingUtils';

export const SnapshotReportFilterSection = () => {
  const { state: snapshotState, dispatch: snapshotDispatch } =
    useContext(AppContext).analyticsSnapshotReport;
  const { state: appDataState } = useContext(AppContext).appData;
  const filterData = snapshotState.analyticsSnapshotReportFilterOptions;
  // Define local state for each filter field
  const [yearSelected, setYearSelected] = useState<string[]>([]);
  const [quarterSelected, setQuarterSelected] = useState<string[]>([]);
  const [monthSelected, setMonthSelected] = useState<string[]>([]);
  const [regionSelected, setRegionSelected] = useState<string[]>(
    appDataState.userRegion ? [appDataState.userRegion] : [],
  );
  const [countrySelected, setCountrySelected] = useState<string[]>([]);
  const [commoditySelected, setCommoditySelected] = useState<string[]>(['PW']);
  const [unitNameSelected, setUnitNameSelected] = useState('TWH');
  const [asiaRegions, setAsiaRegions] = useState<string[]>([]);
  const [transactionTypeSelected, setTransactionTypeSelected] = useState('NCF');
  const [countryOptions, setCountryOptions] = useState<{ label: string; value: string }[]>([]);
  const [reportStatusSelected, setReportStatusSelected] = useState('APPROVED');
  const isAsiaUser =
    localStorage.getItem('isAsiaUser') !== null
      ? localStorage.getItem('isAsiaUser') === 'true'
      : appDataState.userRegionFlags.isAsiaUser;

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetchFilterData();
      let responseData = response.data;
      if (isAsiaUser) {
        const asiaRegionList = responseData.region?.filter((region: string) =>
          region.startsWith('ASIA-')
        );
        setAsiaRegions(asiaRegionList);
        responseData = {
          ...responseData,
          region: asiaRegionList,
        };
        handleCountryOptions(asiaRegionList, [], responseData.regionCountryMapping);
      }
      snapshotDispatch({
        type: 'setAnalyticsSnapshotReportFilterOptions',
        payload: responseData,
      });
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const {
      transactionType,
      commodity,
      countryCode,
      quarter,
      year,
      region,
      uom,
      reportStatus,
      month,
    } = snapshotState.analyticsSnapshotReportFilteredValues;
    setYearSelected(year);
    setQuarterSelected(quarter);
    setMonthSelected(month ?? []); // Provide a default value if month is undefined
    setCountrySelected(countryCode);
    const cleanedRegion = region?.filter((r) => r !== '') ?? [];
    setRegionSelected(cleanedRegion);
    setCommoditySelected(commodity);
    setUnitNameSelected(uom);
    setTransactionTypeSelected(transactionType);
    setReportStatusSelected(reportStatus);
    handleCountryOptions(
      region,
      countryCode,
      snapshotState.analyticsSnapshotReportFilterOptions.regionCountryMapping,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    snapshotState.analyticsSnapshotReportFilteredValues,
    snapshotState.analyticsSnapshotReportFilterOptions.regionCountryMapping,
  ]);

  // Effect to extract Asia regions after API response is available
  useEffect(() => {
    const regionList = snapshotState.analyticsSnapshotReportFilterOptions.region;
    const regionCountryMapping = snapshotState.analyticsSnapshotReportFilterOptions.regionCountryMapping;
    if (isAsiaUser && regionList?.length) {
      const asiaRegionList = regionList.filter((region: string) =>
        region.startsWith('ASIA-')
      );

      setAsiaRegions(asiaRegionList);
      setRegionSelected(asiaRegionList);
      handleCountryOptions(asiaRegionList, [], regionCountryMapping);
    }
  }, [snapshotState.analyticsSnapshotReportFilterOptions.region]);

  if (!filterData) {
    return null; // or a loading spinner
  }

  const handleApplyFilters = () => {
    const updatedRegionSelected =
      isAsiaUser && !regionSelected?.length ? asiaRegions : regionSelected;
    const payload: IAnalyticsSnapshotReportFilteredValues = {
      year: yearSelected,
      quarter: quarterSelected,
      month: monthSelected,
      region: updatedRegionSelected,
      countryCode: countrySelected,
      commodity: Array.isArray(commoditySelected) ? commoditySelected : [commoditySelected],
      uom: unitNameSelected,
      transactionType: transactionTypeSelected,
      reportStatus: reportStatusSelected,
    };

    snapshotDispatch({
      type: 'setAnalyticsSnapshotReportFilteredValues',
      payload,
    });
  };

  const handleClearFilters = () => {
    const userRegion = appDataState.userRegion ? [appDataState.userRegion] : [];
    const updatedRegionSelected = isAsiaUser ? asiaRegions : userRegion;
    // Reset local state
    setYearSelected([]);
    setQuarterSelected([]);
    setMonthSelected([]);
    setRegionSelected(updatedRegionSelected);
    setCountrySelected([]);
    setCommoditySelected(['PW']);
    setUnitNameSelected('TWH');
    setTransactionTypeSelected('NCF');
    setReportStatusSelected('APPROVED');

    // Create a payload with the initial state for each field
    const payload = {
      year: [],
      quarter: [],
      month: [],
      region: updatedRegionSelected,
      countryCode: [],
      commodity: ['PW'],
      uom: 'TWH',
      transactionType: transactionTypeSelected,
      reportStatus: 'APPROVED',
    };

    // Dispatch the action to update the state in your context
    snapshotDispatch({
      type: 'setAnalyticsSnapshotReportFilteredValues',
      payload,
    });
  };

  const handleRegionChange = (filteredRegions: string[]) => {
    const { updatedcountryOptions, updatedFilteredCountries } = getUpdatedCountrySelection(
      filteredRegions,
      countrySelected,
      filterData.regionCountryMapping,
    );
    setRegionSelected(filteredRegions);
    setCountryOptions(updatedcountryOptions);
    setCountrySelected(updatedFilteredCountries);
  };

  const handleCountryOptions = (
    regions: string[],
    countries: string[],
    regionCountryMapping: Record<string, Country[]>,
  ) => {
    const { updatedcountryOptions } = getUpdatedCountrySelection(
      regions,
      countries,
      regionCountryMapping,
    );
    setCountryOptions(updatedcountryOptions);
  };

  return (
    <div data-testid='snapshot-report-filter' style={{ padding: '20px', width: '25%' }}>
      <Flexbox flexDirection='column'>
        <div
          style={{
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '17px',
            lineHeight: '26px',
            color: '#404040',
            padding: '10px 0',
            marginTop: '26px',
            width: '251px',
          }}
        >
          Filters
        </div>
        <SelectFormField
          id='year'
          label='Year'
          testId='Year'
          value={yearSelected}
          onChange={setYearSelected}
          options={filterData.year.map((el: number) => el.toString())}
          mode='multiple'
        />

        <SelectFormField
          id='quarter'
          label='Quarter'
          testId='Quarter'
          value={quarterSelected}
          onChange={setQuarterSelected}
          options={filterData.quarter}
          mode='multiple'
        />

        {quarterSelected.length > 0 && (
          <SelectFormField
            id='month'
            label='Month'
            testId='Month'
            value={monthSelected}
            onChange={setMonthSelected}
            options={getCombinedMonths(quarterSelected)}
            mode='multiple'
          />
        )}

        <SelectFormField
          id='region'
          label='Region'
          testId='Region'
          value={regionSelected}
          onChange={(e: string[]) => handleRegionChange(e)}
          options={filterData.region}
          mode={'multiple'}
          disabled={appDataState.isNcfReportingRegionFieldDisabled}
        />

        <SelectFormField
          id='country'
          label='Country'
          testId='Country'
          value={countrySelected}
          onChange={setCountrySelected}
          options={countryOptions}
          mode='multiple'
        />
        <SelectFormField
          id='commodity'
          label='Commodity'
          testId='Commodity'
          value={commoditySelected}
          onChange={setCommoditySelected}
          mode={'single'}
          options={filterData.commodity.map((el: Commodity) => {
            return {
              label: el.TRADE_NAME,
              value: el.TRADE_CODE,
            };
          })}
        />
        <SelectFormField
          id='transactionType'
          label='Transaction Type'
          testId='Transaction Type'
          value={transactionTypeSelected}
          onChange={setTransactionTypeSelected}
          options={['NCF']}
        />
        <SelectFormField
          id='unitName'
          label='Unit Name'
          testId='Unit Name'
          value={unitNameSelected}
          onChange={setUnitNameSelected}
          options={filterData.unitName}
        />
        <SelectFormField
          id='reportStatus'
          label='Report Status'
          testId='Report Status'
          value={reportStatusSelected}
          onChange={setReportStatusSelected}
          options={filterData.reportStatus}
        />
        <Button className='getDataBtn' data-testid='Apply Filters' onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button
          className='getDataBtn'
          variant='outlined'
          style={{ marginTop: '10px' }}
          data-testid='clear-filters'
          onClick={handleClearFilters}
        >
          Clear Filters
        </Button>
      </Flexbox>
      <div style={{ borderLeft: '1px solid rgba(0, 0, 0, 0.06)' }}></div>
    </div>
  );
};
